import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

import { TOOL_ID, FOLDER_NAME } from '@/consts/configs';

import {
  createNavigationSlice,
  type NavigationSlice,
} from './slices/navigationSlice';
import {
  createLibrarySlice,
  libraryInitialState,
  type LibrarySlice,
} from './slices/librarySlice';
import {
  createPresetSlice,
  presetInitialState,
  type PresetSlice,
} from './slices/presetSlice';
import {
  createInputsSlice,
  inputsInitialState,
  type InputsSlice,
} from './slices/inputsSlice';
import {
  createGenerationSlice,
  generationInitialState,
  type GenerationSlice,
} from './slices/generationSlice';
import {
  createResultSlice,
  resultInitialState,
  type ResultSlice,
} from './slices/resultSlice';
import { createFilesSlice, type FilesSlice } from './slices/filesSlice';
import { createUserSlice, type UserSlice } from './slices/userSlice';
import {
  createProjectSlice,
  projectInitialState,
  type ProjectSlice,
} from './slices/projectSlice';
import { createCanvasSlice, type CanvasSlice } from './slices/canvasSlice';
import { createToastSlice, type ToastSlice } from './slices/toastSlice';
import { createUploadSlice, type UploadSlice } from './slices/uploadSlice';

// a === b for primitive data types, shallow for complex data types
const compare = <T>(a: T, b: T) => a === b || shallow(a, b);

/**
 * Drive constants slice - static values, no setters needed
 */
interface DriveSlice {
  toolId: string;
  folderName: string;
}

/**
 * Reset actions slice - operates across multiple slices
 */
interface ResetSlice {
  resetCreate: () => void;
  resetAll: () => void;
}

/**
 * Combined app state from all slices
 */
export type AppState = NavigationSlice &
  LibrarySlice &
  PresetSlice &
  InputsSlice &
  GenerationSlice &
  ResultSlice &
  FilesSlice &
  UserSlice &
  ProjectSlice &
  CanvasSlice &
  ToastSlice &
  UploadSlice &
  DriveSlice &
  ResetSlice;

export const useAppStore = createWithEqualityFn<AppState>()(
  (...args) => ({
    // Compose all slices
    ...createNavigationSlice(...args),
    ...createLibrarySlice(...args),
    ...createPresetSlice(...args),
    ...createInputsSlice(...args),
    ...createGenerationSlice(...args),
    ...createResultSlice(...args),
    ...createFilesSlice(...args),
    ...createUserSlice(...args),
    ...createProjectSlice(...args),
    ...createCanvasSlice(...args),
    ...createToastSlice(...args),
    ...createUploadSlice(...args),

    // Drive constants - static values
    toolId: TOOL_ID,
    folderName: FOLDER_NAME,

    // Reset actions - operate across multiple slices
    resetCreate: () => {
      const [set] = args;
      set({
        ...presetInitialState,
        ...inputsInitialState,
        ...resultInitialState,
        ...generationInitialState,
        projectCreditsCount: projectInitialState.projectCreditsCount,
        hasAutoSelectedPreset: libraryInitialState.hasAutoSelectedPreset,
      });
    },

    resetAll: () => {
      const [set, , store] = args;
      // Resets entire store to initial state
      // See: https://zustand.docs.pmnd.rs/learn/guides/how-to-reset-state
      set(store.getInitialState(), true);
    },
  }),
  compare
);

export const getAppStore = useAppStore.getState;
export const setAppStore = useAppStore.setState;

export type { AppState as IAppStore };
